import * as Sentry from '@sentry/react'

// Initialize Sentry FIRST — before React, before anything else — so it can
// catch errors from the earliest possible moment. Disabled at runtime when
// VITE_SENTRY_DSN is unset (e.g. local dev without monitoring configured).
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance: sample 10% of transactions
  tracesSampleRate: 0.1,
  // Session Replay: only capture replays on error
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // Don't send PII by default
  sendDefaultPii: false,
  // Filter out chunk-load errors — already handled by the reload logic below
  beforeSend(event) {
    if (event.exception?.values?.some(e =>
      e.value?.includes('Failed to fetch dynamically imported module') ||
      e.value?.includes('Loading chunk')
    )) {
      return null
    }
    return event
  },
})

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { applyThemeBuilderColors } from './lib/themeBuilder'
import './index.css'

applyThemeBuilderColors()

window.addEventListener('storage', () => applyThemeBuilderColors())
window.addEventListener('bwc-theme-change', () => applyThemeBuilderColors())

// ── Prevent Chrome tab discarding ──────────────────────────────────────────
// Chrome aggressively discards background tabs to save memory. For a trading
// terminal that maintains live WebSocket connections and chart state, this
// causes a full page reload when switching back — unacceptable UX. A Web
// Worker with a heartbeat makes Chrome treat the tab as actively communicating
// and skip discard. The worker uses ~0 CPU and ~0 memory.
if (typeof Worker !== 'undefined') {
  try {
    const workerBlob = new Blob(
      ['setInterval(()=>postMessage(""),25000)'],
      { type: 'application/javascript' }
    )
    const keepAliveWorker = new Worker(URL.createObjectURL(workerBlob))
    keepAliveWorker.onmessage = () => {} // Must have a listener
  } catch {
    // Worker creation failed — graceful degradation
  }
}

// Log chunk errors for Sentry but do NOT auto-reload. The app has no
// React.lazy code-split routes, so chunk errors only come from optional
// dynamic imports (simplewebauthn, etc.) that already have try/catch
// handling. A full page reload destroys chart state and feels unstable.
// The keep-alive Worker above prevents Chrome from discarding the tab in
// the first place, eliminating the main source of these errors.
window.addEventListener('error', (e) => {
  if (
    e.message?.includes('Failed to fetch dynamically imported module') ||
    e.message?.includes('Importing a module script failed')
  ) {
    console.warn('[Erevis] Chunk error (not reloading):', e.message)
  }
})
window.addEventListener('unhandledrejection', (e) => {
  if (
    e.reason?.message?.includes('Failed to fetch dynamically imported module') ||
    e.reason?.message?.includes('Importing a module script failed')
  ) {
    console.warn('[Erevis] Chunk rejection (not reloading):', e.reason?.message)
  }
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
)
